<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import { subject, type PrStore } from '../lib/state/pr.svelte';
  import type { TimelineItem } from '../lib/api/queries';
  import { acceptsFrameMessage, authorTrust, briefSandbox, defaultTab, parseBriefTabs, parseOpenMessage, resolveTab } from '../lib/logic/briefTabs';
  import { focusInFrame } from '../lib/keys/typing';
  import { commentKeys, keyboard } from '../lib/keys/keyboard.svelte';
  import { controlCommand } from '../lib/keys/control';
  import { tabListKeys } from '../lib/keys/controls';
  import { canMerge, checksTone, mergeBlockers, mergeExplanation, readiness } from '../lib/logic/readiness';
  import { FOCUS_PARAM, isCard, targetHref, type Card } from '../lib/logic/headerTargets';
  import { collapse, LinkMemo, linkSources, linksIndex, mentionName, mentionText, webKey, type LinkContext, type LinkEntry, type LinkGroup, type Mention, type Place } from '../lib/logic/links';
  import { previewStatusText, previewWhy, type Preview } from '../lib/logic/previews';
  import AttentionControls from '../lib/components/AttentionControls.svelte';
  import RelayPrAgents from '../lib/components/RelayPrAgents.svelte';
  import RelayThread, { type RelayAbout } from '../lib/components/RelayThread.svelte';
  import { relay } from '../lib/api/relay';
  import { relayAgents } from '../lib/state/relayAgents.svelte';
  import { prTask } from '../lib/logic/prAsk';
  import { pullKey } from '../lib/api/relaySubject';
  import { QUESTION_NEEDS, subjectTopic } from '../lib/api/relayTask';
  import LabelList from '../lib/components/LabelList.svelte';
  import Resizer from '../lib/components/Resizer.svelte';
  import { clamp, OVERVIEW, overviewBounds, overviewPerPx, storedSize } from '../lib/logic/resize';
  import { prefs } from '../lib/state/prefs.svelte';
  import { relTime } from '../lib/logic/time';
  import { httpUrl } from '../lib/logic/url';
  import { nav } from '../lib/nav.svelte';
  import { prPath } from '../lib/router';
  import { carryFilter } from '../lib/logic/filefilter';
  import { session } from '../lib/state/session.svelte';
  import { loadDraft, mergeText, saveDraft } from '../lib/state/drafts';
  import Html from '../lib/components/Html.svelte';
  import RefLinks from '../lib/components/RefLinks.svelte';
  import DescNotes from '../lib/components/DescNotes.svelte';
  import PendingTag from '../lib/components/PendingTag.svelte';
  import ReviewEntry from '../lib/components/ReviewEntry.svelte';
  import ConversationComment from '../lib/components/ConversationComment.svelte';
  import { REVIEW_WORD as reviewWord } from '../lib/logic/reviewConversation';
  import { refRequests, refStateText, type RefDetail } from '../lib/logic/refDetails';
  import { loadRefDetails, refDetail } from '../lib/state/refDetails';
  import { bodyHtml } from '../lib/logic/markdown';
  import { aboveLine, headingElements, headingsOf, outline, readingEntry, revealHeading } from '../lib/logic/outline';
  import { commitAuthor, runSummary, timelineRows, type CommitItem } from '../lib/logic/timeline';
  import { commitHref, commitsHref, snapshotHref } from '../lib/logic/snapshotLink';

  interface Props {
    pr: PrStore;
  }
  let { pr }: Props = $props();

  const data = $derived(pr.pr!);

  // ---------------------------------------------------------------- brief tabs
  const trust = $derived(authorTrust(data.authorAssociation));
  const tabs = $derived(
    parseBriefTabs(data.body).map((t) => resolveTab(t, pr.org, import.meta.env.DEV || !!session.config?.dev, trust)),
  );
  /** The tab shown, kept with the history entry so Back returns to it. */
  const BRIEF_TAB_ENTRY = 'briefTab';
  const keptTab = nav.entry<number>(BRIEF_TAB_ENTRY);
  let activeTab = $state(
    untrack(() => (typeof keptTab === 'number' && (keptTab === -1 || (keptTab >= 0 && keptTab < tabs.length)) ? keptTab : defaultTab(trust, tabs))),
  );
  $effect(() => {
    nav.setEntry(BRIEF_TAB_ENTRY, activeTab);
  });
  /** Tab indices in display order: the brief tabs, then the description (-1). */
  const tabOrder = $derived([...tabs.map((_, i) => i), -1]);
  let tabButtons: HTMLButtonElement[] = $state([]);
  const tabId = (i: number) => `brief-tab-${i < 0 ? 'description' : i}`;
  const panelId = (i: number) => `brief-panel-${i < 0 ? 'description' : i}`;

  /** WAI-ARIA tabs: arrow keys, Home and End move between tabs (automatic activation); see `tabListKeys`. */
  function tabKeydown(e: KeyboardEvent) {
    const c = controlCommand(tabListKeys, e);
    if (!c) return;
    const at = tabOrder.indexOf(activeTab);
    const n = tabOrder.length;
    const next = c === 'next' ? (at + 1) % n : c === 'prev' ? (at - 1 + n) % n : c === 'first' ? 0 : n - 1;
    e.preventDefault();
    activeTab = tabOrder[next];
    tabButtons[next]?.focus();
  }
  let frames: HTMLIFrameElement[] = $state([]);

  /** The brief tab whose embedded page has the focus, so its keys never reach the page's shortcuts; -1 when none has. */
  const frameKeys = $derived(keyboard.frame ? frames.indexOf(keyboard.frame as HTMLIFrameElement) : -1);
  const pausedNote = (title: string) => `Shortcuts are paused while ${title} has the focus. Click outside it, or Tab out of it, to use them again.`;

  onMount(() => {
    void pr.loadFiles();
    const onMessage = (e: MessageEvent) => {
      const i = frames.findIndex((f) => f && f.contentWindow === e.source);
      if (i < 0) return;
      if (!acceptsFrameMessage(tabs[i], e.origin)) return;
      const files = new Set((pr.files ?? []).map((f) => f.filename));
      const msg = parseOpenMessage(e.data, files);
      if (!msg) return;
      const q = new URLSearchParams({ file: msg.path });
      if (msg.line) q.set('line', String(msg.line));
      // The frame holding the focus goes with this view: bring the focus back to this document (on the frame's tab) first, or the keys reach no document in Files.
      if (focusInFrame(document)) tabButtons[tabOrder.indexOf(i)]?.focus({ preventScroll: true });
      nav.go(carryFilter(prPath(pr.owner, pr.repo, pr.number, 'files') + '?' + q, nav.query));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  });

  // ---------------------------------------------------------------- columns
  /**
   * Three columns (outline and Links, the description and conversation,
   * the merge and review cards) from this width; two below it, as
   * views.css lays them out.
   */
  const THREE_COLUMNS = '(min-width: 1500px)';
  const wideNow = () => typeof window !== 'undefined' && !!window.matchMedia && matchMedia(THREE_COLUMNS).matches;
  let wide = $state(wideNow());
  onMount(() => {
    if (!window.matchMedia) return;
    const mq = matchMedia(THREE_COLUMNS);
    wide = mq.matches;
    const change = (e: MediaQueryListEvent) => (wide = e.matches);
    mq.addEventListener?.('change', change);
    return () => mq.removeEventListener?.('change', change);
  });

  /**
   * The side columns' widths: the reader's, else views.css's. They are set on
   * the root element, where the header above the columns reads them too. A
   * drag shows its width live (`live`) and stores it when it ends. Each is
   * bounded by the Overview's width (`ovW`), so the middle column keeps its
   * minimum: the left column beside the right one's stored width, the right
   * beside the left one's bounded width.
   */
  type Side = 'left' | 'right' | 'side';
  const PREF = { left: 'overviewLeft', right: 'overviewRight', side: 'overviewSide' } as const;
  let live: Partial<Record<Side, number>> = $state({});
  let overviewEl: HTMLElement | undefined = $state();
  let ovW = $state(0);
  $effect(() => {
    const el = overviewEl;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => (ovW = el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const stored = (k: Side) => live[k] ?? storedSize(prefs.value[PREF[k]], OVERVIEW[k]);
  const unbounded = (k: Side) => clamp(stored(k) ?? OVERVIEW[k].default, OVERVIEW[k]);
  const bounds = (k: Side) => overviewBounds(k, ovW || Infinity, k === 'left' ? unbounded('right') : k === 'right' ? width('left') : 0);
  const width = (k: Side): number => clamp(stored(k) ?? OVERVIEW[k].default, bounds(k));
  $effect(() => {
    const root = document.documentElement.style;
    for (const k of ['left', 'right', 'side'] as const) if (stored(k) !== null) root.setProperty(`--ov-${k}`, `${width(k)}px`);
    return () => {
      for (const k of ['left', 'right', 'side'] as const) root.removeProperty(`--ov-${k}`);
    };
  });
  function storeWidth(k: Side, v: number | null) {
    delete live[k];
    if (prefs.value[PREF[k]] !== v) prefs.set(PREF[k], v);
  }
  /** Column width per pixel of pointer travel: two while the columns are centred with room to spare. */
  function perPx(): number {
    const o = overviewEl?.getBoundingClientRect();
    const cols = overviewEl ? [...overviewEl.children].filter((c) => c.matches('.ov-rail, .ov-main')).map((c) => c.getBoundingClientRect()) : [];
    if (!o || !cols.length) return 1;
    return overviewPerPx(o.width, Math.max(...cols.map((c) => c.right)) - Math.min(...cols.map((c) => c.left)));
  }

  // ---------------------------------------------------------------- outline
  /** The description's headings, read from the HTML it renders, so the outline lists them while a brief tab hides it. */
  const entries = $derived(data.body?.trim() ? outline(headingsOf(bodyHtml(data.bodyHTML, data.body))) : []);
  let descriptionBody: HTMLElement | null = $state(null);
  /** The outline's height: the Links card sticks below it. */
  let outlineH = $state(0);
  let outlineEl: HTMLElement | null = $state(null);
  $effect(() => {
    const el = outlineEl;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => (outlineH = el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  });
  /** The outline entry being read (scroll spy); -1 before any is. */
  let reading = $state(-1);

  /** The description's heading elements, by heading index; none while it is hidden. */
  const headingEls = () => (descriptionBody ? headingElements(descriptionBody) : []);

  /**
   * Scroll spy. An IntersectionObserver whose root runs from the reading
   * line (a little below the top bar) far down the page reports whenever
   * a heading or the description's end crosses that line, on scroll and
   * on reflow alike, however far a jump goes; only then are the headings
   * measured, all of them (one revealed from a closed `<details>` crosses
   * nothing), and `readingEntry` marks the last one above the line.
   */
  $effect(() => {
    const desc = descriptionBody;
    const list = entries;
    if (!wide || !desc || !list.length || typeof IntersectionObserver === 'undefined') {
      reading = -1;
      return;
    }
    const line = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--topbar-h')) || 0) + 72;
    const els = headingElements(desc);
    const watched = list.map((e) => els[e.index]).filter((el): el is HTMLElement => !!el);
    const measure = () => {
      const end = desc.getBoundingClientRect();
      const past = !!end.height && end.bottom <= line;
      reading = readingEntry(
        list.map((e) => !!els[e.index] && aboveLine(els[e.index].getBoundingClientRect(), line)),
        past,
      );
    };
    const io = new IntersectionObserver(measure, { rootMargin: `-${line}px 0px 100000px 0px` });
    io.observe(desc);
    for (const el of watched) io.observe(el);
    return () => io.disconnect();
  });

  /** The heading an outline entry led to, briefly highlighted. */
  let flashedHeading: HTMLElement | null = null;
  let headingTimer: ReturnType<typeof setTimeout> | null = null;
  onMount(() => () => {
    if (headingTimer) clearTimeout(headingTimer);
  });

  /** Show heading `index` of the description (from a brief tab, or a closed `<details>`, too), focusing it. */
  async function goToHeading(index: number) {
    activeTab = -1;
    await tick();
    const el = headingEls()[index];
    if (!el) return;
    revealHeading(el);
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.tabIndex = -1;
    el.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' });
    el.focus({ preventScroll: true });
    flashedHeading?.classList.remove('heading-flash');
    el.classList.add('heading-flash');
    flashedHeading = el;
    if (headingTimer) clearTimeout(headingTimer);
    headingTimer = setTimeout(() => el.classList.remove('heading-flash'), 1800);
  }

  // ---------------------------------------------------------------- reviewers & attention
  const requested = $derived(
    data.reviewRequests.nodes
      .map((n) => n.requestedReviewer)
      .filter((r) => !!r)
      .map((r) => (r!.__typename === 'Team' ? '@' + r!.slug : r!.login)),
  );
  let newReviewer = $state('');
  /** Manual attention changes the viewer may undo (all of them, or none). */
  const canClearOverrides = $derived(pr.overrides.length > 0 && pr.overrides.every((o) => pr.canSetAttention(o.login)));
  const readOnlyTitle = $derived(pr.readOnlyText ?? undefined);

  // ---------------------------------------------------------------- header targets (?focus=<card>)
  /** The card being highlighted after a header item led here. */
  let flashed: Card | null = $state(null);
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  $effect(() => {
    const card = nav.query.get(FOCUS_PARAM);
    if (card === null) return;
    void tick().then(() => {
      const el = isCard(card) ? document.getElementById(`card-${card}`) : null;
      if (el && isCard(card)) {
        const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
        el.querySelector<HTMLElement>('h3')?.focus({ preventScroll: true });
        flashed = card;
        if (flashTimer) clearTimeout(flashTimer);
        flashTimer = setTimeout(() => (flashed = null), 1800);
      }
      // Done: a later click on the same header item adds the parameter again.
      nav.setQuery({ [FOCUS_PARAM]: null }, { replace: true });
    });
  });
  onMount(() => () => {
    if (flashTimer) clearTimeout(flashTimer);
  });

  // ---------------------------------------------------------------- links
  /** Entries the Links card shows before "Show all". */
  const LINKS_SHOWN = 5;
  /** Entries (by key) listing their mentions; the others show only how many there are. */
  let openMentions: Set<string> = $state(new Set());
  function toggleMentions(key: string) {
    const next = new Set(openMentions);
    if (!next.delete(key)) next.add(key);
    openMentions = next;
  }
  /** The count button's name; `aria-expanded` says whether the mentions show. */
  const mentionsToggleLabel = (e: LinkEntry) => `${e.mentions.length} mention${e.mentions.length === 1 ? '' : 's'} of ${e.label}`;
  /** Each conversation item's links, read again only when it changes. */
  const linkMemo = new LinkMemo();
  /** How links read: this pull request, its GitHub, its commits and the references whose titles are known. */
  const linkCtx: LinkContext = $derived.by(() => {
    const headlines = new Map(pr.timeline.flatMap((it) => (it.__typename === 'PullRequestCommit' ? [[it.commit.oid, it.commit.messageHeadline] as const] : [])));
    return {
      pr: { owner: pr.owner, repo: pr.repo, number: pr.number },
      githubWebUrl: session.config?.github_web_url ?? 'https://github.com',
      commits: pr.snapshots.map((s) => ({ sha: s.sha, title: headlines.get(s.sha) ?? null })),
      known: pr.knownRefs,
    };
  });
  const allLinkGroups = $derived(linksIndex(linkSources(data, pr.timeline, pr.threads), linkCtx, linkMemo));
  /**
   * The previews (deployments' and those linked in comments), first and in
   * a group of their own; each carries the mentions of its link, which
   * leaves the other groups.
   */
  const previewEntries = $derived.by(() => {
    const byKey = new Map(allLinkGroups.flatMap((g) => g.entries.map((e) => [e.key, e] as const)));
    return pr.previews.map((p) => {
      const key = p.url ? webKey(p.url) : `preview:${p.label}`;
      const linked = byKey.get(key);
      const entry: LinkEntry = {
        key,
        target: p.url ? { kind: 'web', url: p.url, site: p.host } : { kind: 'web', url: '', site: '' },
        label: p.url ? (p.url === p.logUrl ? `${p.label} log` : p.host) : p.label,
        title: null,
        open: { kind: 'external', url: p.url ?? '' },
        mentions: linked?.mentions ?? [],
      };
      return { entry, preview: p };
    });
  });
  const linkGroups: LinkGroup[] = $derived.by(() => {
    const taken = new Set(previewEntries.map((p) => p.entry.key));
    return allLinkGroups.map((g) => ({ ...g, entries: g.entries.filter((e) => !taken.has(e.key)) })).filter((g) => g.entries.length);
  });
  /** Why a preview stands as it does, for its status chip's tooltip. */
  const previewNote = (p: Preview) => previewWhy(p);
  const linkCount = $derived(previewEntries.length + linkGroups.reduce((n, g) => n + g.entries.length, 0));

  /** Bumped as reference details arrive, so the card reads them again. */
  let detailsRead = $state(0);
  // Kind, title and state of the card's references (and commits' subjects), asked for in one request.
  $effect(() => {
    const reqs = refRequests(linkGroups);
    if (reqs.length) void loadRefDetails(reqs).then(() => detailsRead++);
  });
  const detailOf = (e: LinkEntry): RefDetail | null | undefined => {
    void detailsRead;
    return e.target.kind === 'ref' || e.target.kind === 'commit' ? refDetail(e.key) : null;
  };
  let allLinks = $state(false);
  const shownLinks = $derived(allLinks ? { groups: linkGroups, hidden: 0 } : collapse(linkGroups, Math.max(0, LINKS_SHOWN - previewEntries.length)));

  /** Where a link in the card goes: a changelist route (keeping the file filter), or its own page. */
  const linkHref = (e: LinkEntry) => (e.open.kind === 'app' ? carryFilter(e.open.path, nav.query) : e.open.url);

  /** The element a description or conversation mention jumps to. */
  const descriptionId = $derived(tabs.length ? panelId(-1) : 'pr-description');
  const placeId = (p: Exclude<Place, { kind: 'thread' }>) => (p.kind === 'description' ? descriptionId : `tl-${p.id}`);
  /** The element last jumped to, highlighted briefly. */
  let jumped: string | null = $state(null);
  let jumpTimer: ReturnType<typeof setTimeout> | null = null;

  /** Show a mention in the description or the conversation: scroll to it, highlight it and focus it. */
  async function jump(p: Exclude<Place, { kind: 'thread' }>) {
    if (p.kind === 'description') activeTab = -1;
    await tick();
    const id = placeId(p);
    const el = document.getElementById(id);
    if (!el) return;
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
    el.focus({ preventScroll: true });
    jumped = id;
    if (jumpTimer) clearTimeout(jumpTimer);
    jumpTimer = setTimeout(() => (jumped = null), 1800);
  }
  onMount(() => () => {
    if (jumpTimer) clearTimeout(jumpTimer);
  });
  /** The comments on passages of the description. */
  let descNotes: DescNotes | null = $state(null);
  const descText = $derived((descriptionBody as HTMLElement | null)?.querySelector<HTMLElement>('.comment-body') ?? null);
  /** Show the passage a comment on the description quotes, or the description when its words are gone. */
  async function showPassage(id: string) {
    activeTab = -1;
    await tick();
    if (!(await descNotes?.reveal(id))) void jump({ kind: 'description' });
  }
  const mentionLabel = (m: Mention) => mentionName(m, relTime(m.at));
  const mentionChip = (m: Mention) => mentionText(m, relTime(m.at));

  // ---------------------------------------------------------------- merge
  /** The org's merge setting; merging stays off until the policy is read. */
  const allowMerge = $derived(pr.org.status === 'ready' && pr.org.settings.allow_merge);
  const mergeAllowed = $derived(canMerge(data, allowMerge));
  const ready = $derived(readiness(data, pr.unresolvedCount, !pr.partial));
  /** What readiness still lacks although GitHub would merge (e.g. no approval on an unprotected branch). */
  const missing = $derived(ready.ready ? [] : mergeBlockers(data, pr.unresolvedCount, !pr.partial));
  /** Why it cannot be merged from here, when the merge button is not offered. */
  const whyNot = $derived(
    mergeAllowed
      ? []
      : pr.org.status === 'ready'
        ? mergeExplanation(data, pr.unresolvedCount, allowMerge)
        : [
            ...mergeExplanation(data, pr.unresolvedCount, true),
            pr.org.status === 'pending' ? "Organization policy hasn't been resolved yet." : 'Organization merge policy not loaded.',
          ],
  );
  /** The merge in flight, if any (the card dims and says so). */
  const merging = $derived(pr.writes.on(subject.merge));
  function mergeNow() {
    const label = methods.find((m) => m![0] === method)?.[1] ?? 'Merge';
    const warning = missing.length ? `\n\nThis pull request is not ready: ${missing.join('; ')}.` : '';
    if (confirm(`${label}: merge #${pr.number} into ${data.baseRefName}?${warning}`)) void pr.merge(method);
  }
  const methods = $derived(
    [
      pr.repository?.squashMergeAllowed ? (['SQUASH', 'Squash and merge'] as const) : null,
      pr.repository?.mergeCommitAllowed ? (['MERGE', 'Create a merge commit'] as const) : null,
      pr.repository?.rebaseMergeAllowed ? (['REBASE', 'Rebase and merge'] as const) : null,
    ].filter((x) => !!x),
  );
  let method: 'MERGE' | 'SQUASH' | 'REBASE' = $state('SQUASH');
  $effect(() => {
    if (methods.length && !methods.some((m) => m![0] === method)) method = methods[0]![0];
  });

  // ---------------------------------------------------------------- conversation
  let comment = $state('');
  const draftKey = $derived(`issue:${pr.owner}/${pr.repo}#${pr.number}`);
  // The box offers its saved text (and any a closed page's unsent comment
  // left), unless a comment being sent or its Retry holds it.
  onMount(() => {
    void loadDraft(draftKey, { outbox: !pr.writes.holds(draftKey) }).then((b) => (comment = mergeText(comment, b)));
  });
  // A failed comment given up comes back, beside what was typed since.
  $effect(() => pr.writes.listen(draftKey, (t) => (comment = mergeText(comment, t))));

  /**
   * Post the comment: it shows in the conversation at once, marked as being
   * posted, and the box empties (so a repeated Ctrl/Cmd+Enter posts nothing).
   * If posting fails it is taken out again and the toast's Retry keeps its
   * own copy; the box is free meanwhile, and the text comes back beside
   * anything typed since when the Retry is given up.
   */
  function postComment() {
    if (!comment.trim() || pr.readOnly) return;
    void pr.addIssueComment(comment, { key: draftKey, text: comment });
    comment = '';
  }

  /**
   * A comment on the whole pull request is part of the review by default:
   * held with the other drafts (pr/draftWrites `addPrComment`) and sent in
   * the review's body when it is ticked and published. Posting it to the
   * conversation at once is the other button's (Post now). Where drafts
   * can't be held, the box posts, as it did.
   */
  const toReview = $derived(!pr.draftsOff);
  function addToReview() {
    if (!comment.trim() || pr.draftsOff) return;
    void pr.addPrComment(comment, { key: draftKey, text: comment });
    comment = '';
  }
  const submitComment = () => (toReview ? addToReview() : postComment());

  // ---------------------------------------------------------------- asking an agent about the pull request
  /** The connected agents that can be asked (their grant here covers what a question carries: QUESTION_NEEDS). */
  const askable = $derived(!session.agents ? [] : relayAgents.taskAgents(pullKey(pr.owner, pr.repo, pr.number), QUESTION_NEEDS));
  const prAbout: RelayAbout = $derived({
    topic: subjectTopic(pullKey(pr.owner, pr.repo, pr.number)),
    task: (i: string, quote?: string | null) => prTask(pr, i, quote ?? null),
    needs: QUESTION_NEEDS,
    carries: 'the pull request’s description and state',
    canDraft: !pr.draftsOff,
    draft: async (text: string, key: string) => !!(await pr.addPrComment(text, { key, text })),
    draftTitle: 'Make this answer your comment on the pull request, held as a draft for your review',
    noun: 'question',
    asksHere: false,
  });
  /** The agent conversation shows: asked from this tab, or from this browser before (relay.sentTo). */
  let agentOpen = $state(false);
  onMount(() => { if (session.agents) void relay.sentTo(subjectTopic(pullKey(pr.owner, pr.repo, pr.number))).then((ids) => { if (ids.length) agentOpen = true; }, () => {}); });
  let agentPanel: RelayThread | undefined = $state();
  let asking = $state(false);
  let askError = $state('');
  /** Ask `to` what the box says (or `from`, a passage's comment): a question about the pull request, answered privately below the box. */
  async function askAgent(to: string, from: { text: string; quote: string } | null = null) {
    const text = from ? from.text.trim() : comment.trim();
    if (!text || asking) return false;
    asking = true;
    askError = '';
    agentOpen = true;
    await tick();
    try {
      await agentPanel?.askNow(to, text, from ? from.quote : null);
      if (!from) {
        comment = '';
        saveDraft(draftKey, '');
      }
      return true;
    } catch (e) {
      askError = e instanceof Error ? e.message : String(e);
      return false;
    } finally {
      asking = false;
    }
  }

  /** The conversation's rows: runs of commits fold (timelineRows). */
  const rows = $derived(timelineRows(pr.timeline));
  const ref = $derived({ owner: pr.owner, repo: pr.repo, number: pr.number });
  /** A force-push side's link: the Files view at that snapshot (base → it), and its name. */
  function snapshotLink(sha: string): { href: string; title: string } {
    const s = pr.snapshots.find((x) => x.sha === sha);
    const name = s ? `snapshot ${s.n} (base → ${s.short})` : sha.slice(0, 7);
    return { href: snapshotHref(ref, { sha, head: data.headRefOid }, nav.query), title: `Open the Files view at ${name}` };
  }
  const runOpen = (key: string) => !!pr.prefs.data.openRuns[key];
  // Unfolded runs the conversation no longer has (a force-push rewrote them) are forgotten.
  $effect(() => {
    const keys = rows.flatMap((r) => (r.kind === 'run' ? [r.key] : []));
    untrack(() => pr.prefs.keepRuns(keys));
  });

  function itemAt(it: TimelineItem): string {
    if (it.__typename === 'PullRequestCommit') return it.commit.committedDate;
    if (it.__typename === 'PullRequestReview') return it.submittedAt ?? it.createdAt;
    return it.createdAt;
  }

  const words: Record<string, string> = {
    ReadyForReviewEvent: 'marked this ready for review',
    ConvertToDraftEvent: 'converted this to a draft',
    MergedEvent: 'merged this pull request',
    ClosedEvent: 'closed this pull request',
    ReopenedEvent: 'reopened this pull request',
  };
  const checkName = (c: (typeof pr.checks)[number]) =>
    c.__typename === 'CheckRun'
      ? `${c.checkSuite?.workflowRun?.workflow.name ? c.checkSuite.workflowRun.workflow.name + ' / ' : ''}${c.name}`
      : (c.context ?? 'status');
  const checkState = (c: (typeof pr.checks)[number]) =>
    c.__typename === 'CheckRun' ? (c.status === 'COMPLETED' ? (c.conclusion ?? 'COMPLETED') : (c.status ?? '')) : (c.state ?? '');
</script>

{#snippet commitRow(it: CommitItem)}
  <li class="tl-event tl-commit">
    <span class="dot"></span>
    <a class="tl-sha" href={commitHref(ref, it.commit.oid, nav.query)} title="Open this commit's changes in Files"><code>{it.commit.abbreviatedOid}</code></a>
    <span class="tl-text">{it.commit.messageHeadline}</span>
    <span class="muted">· {commitAuthor(it)} {relTime(it.commit.committedDate)}</span>
    <a class="tl-github" href="{data.url}/commits/{it.commit.oid}" target="_blank" rel="noreferrer" title="This commit on GitHub" aria-label="{it.commit.abbreviatedOid} on GitHub (opens in a new tab)">↗</a>
  </li>
{/snippet}

{#snippet shaLink(sha: string, short: string)}
  {@const l = snapshotLink(sha)}
  <a class="tl-sha" href={l.href} title={l.title}><code>{short}</code></a>
{/snippet}

{#snippet outlineNav()}
  {#if entries.length}
    <nav class="side-card outline" aria-label="Description outline">
      <h3>Outline</h3>
      <ol>
        {#each entries as e, k (e.index)}
          <li class:outermost={e.depth === 0} style:--depth={e.depth}>
            <button type="button" class:current={reading === k} aria-current={reading === k ? 'location' : undefined} onclick={() => goToHeading(e.index)}>{e.text}</button>
          </li>
        {/each}
      </ol>
    </nav>
  {/if}
{/snippet}

{#snippet linkEntry(e: LinkEntry, mentionsId: string, preview: Preview | null)}
  {@const open = openMentions.has(e.key)}
  {@const d = preview ? null : detailOf(e)}
  {@const linkTitle = e.title ?? d?.title ?? null}
  <li class="link-entry">
    <span class="link-head">
      {#if e.open.kind === 'app'}
        <a class="link-target" href={linkHref(e)} title={e.title ?? undefined}>{e.label}</a>
      {:else if !e.open.url}
        <span class="link-target">{e.label}</span>
      {:else if e.target.kind === 'mail'}
        <a class="link-target" href={linkHref(e)} rel="noopener noreferrer">{e.label}</a>
      {:else}
        <a class="link-target" href={linkHref(e)} target="_blank" rel="noopener noreferrer" title={e.open.url}>{e.label}<span class="ext" aria-hidden="true">↗</span><span class="sr-only"> (opens in a new tab)</span></a>
      {/if}
      {#if linkTitle}<span class="link-title" title={linkTitle}>{linkTitle}</span>{/if}
      {#if preview}<span class="ref-state pv-{preview.status}" title={previewNote(preview)}>{previewStatusText(preview)}</span><span class="sr-only">, {previewNote(preview)}</span>
      {:else if d && d.kind !== 'commit'}<span class="ref-state {d.state}">{refStateText(d)}</span>{/if}
      {#if e.mentions.length}<button
        type="button"
        class="mentions-toggle"
        class:open
        aria-expanded={open}
        aria-controls={open ? mentionsId : undefined}
        aria-label={mentionsToggleLabel(e)}
        onclick={() => toggleMentions(e.key)}>{e.mentions.length}</button
      >{/if}
    </span>
    {#if open}
      <ul class="link-mentions" id={mentionsId} aria-label="Where {e.label} is mentioned">
        {#each e.mentions as m, k (k)}
          <li>
            {#if m.place.kind === 'thread'}
              <a class="mention" href={targetHref({ kind: 'thread', thread: m.place.thread, comment: m.place.comment }, pr, nav.query)} aria-label={mentionLabel(m)}
                >{mentionChip(m)}</a
              >
            {:else}
              {@const place = m.place}
              <button type="button" class="mention" onclick={() => jump(place)} aria-label={mentionLabel(m)}
                >{mentionChip(m)}</button
              >
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  </li>
{/snippet}

{#snippet linksCard()}
  <section id="card-links" class="side-card links" aria-labelledby="card-links-h">
    <h3 id="card-links-h" tabindex="-1">Links <span class="muted links-count">({linkCount})</span></h3>
    {#if !linkCount}
      <p class="muted small">No links in this conversation.</p>
    {:else}
      <ul class="link-groups">
        {#if previewEntries.length}
          <li>
            <h4 class="link-group" id="links-previews">Previews</h4>
            <ul class="link-entries" aria-labelledby="links-previews">
              {#each previewEntries as pe, pi (pe.entry.key)}
                {@render linkEntry(pe.entry, `link-mentions-pv-${pi}`, pe.preview)}
              {/each}
            </ul>
          </li>
        {/if}
        {#each shownLinks.groups as g, gi (g.key)}
          <li>
            <h4 class="link-group" id="links-{g.key}">{g.label}</h4>
            <ul class="link-entries" aria-labelledby="links-{g.key}">
              {#each g.entries as e, ei (e.key)}
                {@render linkEntry(e, `link-mentions-${gi}-${ei}`, null)}
              {/each}
            </ul>
          </li>
        {/each}
      </ul>
      {#if linkCount > LINKS_SHOWN}
        <button type="button" class="linkish links-more" aria-expanded={allLinks} onclick={() => (allLinks = !allLinks)}
          >{allLinks ? 'Show fewer' : `Show all ${linkCount}`}</button
        >
      {/if}
    {/if}
  </section>
{/snippet}

<div class="overview" class:three={wide} class:outlined={wide && entries.length > 0} style:--outline-h={wide && entries.length ? `${outlineH}px` : undefined} bind:this={overviewEl}>
  {#snippet resizer(k: Side, label: string)}
    <Resizer
      class="ov-resizer ov-resizer-{k}"
      {label}
      value={width(k)}
      min={bounds(k).min}
      max={bounds(k).max}
      step={OVERVIEW.step}
      direction={k === 'left' ? 1 : -1}
      valuetext={(v) => `${v} pixels`}
      {perPx}
      controls={k === 'left' ? (entries.length ? 'ov-outline ov-links' : 'ov-links') : 'ov-cards'}
      oninput={(v) => (v === null ? delete live[k] : (live[k] = v))}
      onchange={(v) => storeWidth(k, v)}
      onreset={() => storeWidth(k, null)}
    />
  {/snippet}
  {#if wide && entries.length}
    <!-- The left column is split so the document (and tab) order is outline, description, Links: the grid places both parts on the left. -->
    <div class="ov-outline ov-rail" id="ov-outline" bind:this={outlineEl}>
      {@render outlineNav()}
    </div>
  {/if}
  <div class="ov-main">
    {#if tabs.length}
      <section class="brief" aria-label="Author brief">
        <!-- svelte-ignore a11y_interactive_supports_focus -->
        <div class="brief-tabs" role="tablist" aria-label="Brief" tabindex="-1" onkeydown={tabKeydown}>
          {#each tabOrder as i, k (i)}
            <button
              bind:this={tabButtons[k]}
              role="tab"
              id={tabId(i)}
              aria-controls={panelId(i)}
              aria-selected={activeTab === i}
              tabindex={activeTab === i ? 0 : -1}
              class:active={activeTab === i}
              onclick={() => (activeTab = i)}>{i < 0 ? 'Description' : tabs[i].title}</button
            >
          {/each}
        </div>
        {#each tabs as t, i}
          <div class="brief-panel" role="tabpanel" id={panelId(i)} aria-labelledby={tabId(i)} hidden={activeTab !== i}>
            <div class="brief-origin">
              <span class="eyebrow">Content from</span>
              <code>{t.origin ?? t.src}</code>
              {#if t.allowed && t.display === 'embed'}
                <a href={t.src} target="_blank" rel="noreferrer">Open ↗</a>
                <!-- Room for the note is always kept (by a copy that only takes space), so showing it never covers the page or moves the frame. -->
                <span class="frame-keys-slot">
                  <span class="frame-keys frame-keys-sizer" aria-hidden="true">{pausedNote(t.title)}</span>
                  <span role="status">{#if frameKeys === i}<span class="frame-keys">{pausedNote(t.title)}</span>{/if}</span>
                </span>
              {/if}
            </div>
            {#if t.allowed && t.display === 'link'}
              <div class="brief-link">
                {#if t.reason}
                  <p>{t.reason} You can open {t.title} in a new tab.</p>
                {:else}
                  <p>{t.title} is published at <code>{t.origin}</code>, which doesn't allow embedding.</p>
                {/if}
                <a class="btn" href={t.src} target="_blank" rel="noopener noreferrer">Open {t.title} ↗</a>
              </div>
            {:else if t.allowed}
              <p class="brief-hint">Blank below? The site may not allow embedding. <a href={t.src} target="_blank" rel="noreferrer">Open it in a new tab ↗</a></p>
              <iframe
                bind:this={frames[i]}
                title={t.title}
                src={t.src}
                sandbox={briefSandbox(t.origin, location.origin)}
                referrerpolicy="no-referrer"
                loading="lazy"
              ></iframe>
            {:else}
              <div class="brief-blocked"><b>This tab is blocked.</b> {t.reason}</div>
            {/if}
          </div>
        {/each}
      </section>
    {/if}

    {#if !tabs.length || activeTab === -1}
      <section
        class="card description"
        bind:this={descriptionBody}
        class:jump-flash={jumped === descriptionId}
        role={tabs.length ? 'tabpanel' : undefined}
        id={descriptionId}
        tabindex="-1"
        aria-label={tabs.length ? undefined : 'Description'}
        aria-labelledby={tabs.length ? tabId(-1) : undefined}
      >
        <div class="comment-head">
          <b>{data.author?.login ?? 'ghost'}</b><span>opened {relTime(data.createdAt)}</span>
        </div>
        {#if data.body?.trim()}
          <Html html={data.bodyHTML} md={data.body} class="comment-body markdown" />
        {:else}
          <p class="muted">No description provided.</p>
        {/if}
      </section>
    {/if}

    <section class="conversation" aria-label="Conversation" aria-busy={pr.partial}>
      <h2 class="section-title">Conversation</h2>
      <ol class="timeline">
        {#if pr.partial}
          <!-- The newest page is shown; the older activity is still being read. -->
          <li class="tl-event tl-truncated">
            <span class="dot"></span>
            <span class="counting-note">Older activity is still loading…</span>
          </li>
        {/if}
        {#if pr.timelineTruncated}
          <li class="tl-event tl-truncated">
            <span class="dot"></span>
            <span class="muted">Older activity is not shown here. <a href={data.url} target="_blank" rel="noreferrer">See the full timeline on GitHub ↗</a></span>
          </li>
        {/if}
        {#each rows as row (row.kind === 'run' ? 'run:' + row.key : row.item.id)}
          {#if row.kind === 'run'}
            {@const sum = runSummary(row.commits)}
            {@const open = runOpen(row.key)}
            <li class="tl-event tl-run" class:open>
              <span class="dot"></span>
              <button type="button" class="run-toggle" aria-expanded={open} aria-controls={open ? `run-${row.key}` : undefined} onclick={() => pr.prefs.setRunOpen(row.key, !open)}
                >{sum.text}</button
              >
              <span class="muted">·</span>
              <a class="run-diff" href={commitsHref(ref, sum.first, sum.last, nav.query)} title={sum.diffTitle}>combined diff</a>
            </li>
            {#if open}
              <li class="tl-run-body">
                <ol class="run-commits" id="run-{row.key}" aria-label="{row.commits.length} commits">
                  {#each row.commits as c (c.id)}{@render commitRow(c)}{/each}
                </ol>
              </li>
            {/if}
          {:else}
          {@const it = row.item}
          {#if it.__typename === 'IssueComment'}
            {@const saving = pr.writes.on(subject.comment(it.id))}
            <li id="tl-{it.id}" tabindex="-1" class="tl-card" class:jump-flash={jumped === `tl-${it.id}`} class:is-pending={!!saving} aria-busy={saving ? 'true' : undefined}>
              <ConversationComment {pr} comment={it} onpassage={showPassage} />
            </li>
          {:else if it.__typename === 'PullRequestReview' && it.state !== 'PENDING'}
            {#if it.body.trim() || it.comments.totalCount}
              <!-- A review with inline comments is a card, so they unfold inside it. -->
              <li id="tl-{it.id}" tabindex="-1" class="tl-card review-{it.state.toLowerCase()}" class:jump-flash={jumped === `tl-${it.id}`}>
                <ReviewEntry {pr} review={it} />
              </li>
            {:else}
              <li class="tl-event">
                <span class="dot {it.state === 'APPROVED' ? 'good' : it.state === 'CHANGES_REQUESTED' ? 'bad' : ''}"></span>
                <b>{it.author?.login ?? 'ghost'}</b>
                {reviewWord[it.state] ?? it.state}{#if it.comments.totalCount}&nbsp;with {it.comments.totalCount} comment{it.comments.totalCount === 1 ? '' : 's'}{/if}
                <span class="muted">· {relTime(itemAt(it))}</span>
              </li>
            {/if}
          {:else if it.__typename === 'PullRequestCommit'}
            {@render commitRow(it)}
          {:else if it.__typename === 'HeadRefForcePushedEvent' || it.__typename === 'BaseRefForcePushedEvent'}
            <li class="tl-event tl-forcepush">
              <span class="dot"></span>
              <b>{it.actor?.login ?? 'someone'}</b> force-pushed {it.__typename === 'BaseRefForcePushedEvent' ? 'the base branch' : ''}
              {#if it.beforeCommit && it.afterCommit}
                {#if it.__typename === 'HeadRefForcePushedEvent'}
                  from {@render shaLink(it.beforeCommit.oid, it.beforeCommit.abbreviatedOid)} to {@render shaLink(it.afterCommit.oid, it.afterCommit.abbreviatedOid)}
                  · <a class="interdiff" href={carryFilter(`${prPath(pr.owner, pr.repo, pr.number, 'files')}?from=${it.beforeCommit.oid}&to=${it.afterCommit.oid}`, nav.query)}>interdiff</a>
                {:else}
                  from <code>{it.beforeCommit.abbreviatedOid}</code> to <code>{it.afterCommit.abbreviatedOid}</code>
                {/if}
              {/if}
              <span class="muted">· {relTime(it.createdAt)}</span>
            </li>
          {:else if it.__typename === 'ReviewRequestedEvent' || it.__typename === 'ReviewRequestRemovedEvent'}
            <li class="tl-event">
              <span class="dot"></span>
              <b>{it.actor?.login ?? 'someone'}</b>
              {it.__typename === 'ReviewRequestedEvent' ? 'requested a review from' : 'removed the review request for'}
              <b>{it.requestedReviewer ? (it.requestedReviewer.__typename === 'Team' ? '@' + it.requestedReviewer.slug : it.requestedReviewer.login) : 'someone'}</b>
              <span class="muted">· {relTime(it.createdAt)}</span>
            </li>
          {:else if it.__typename !== 'PullRequestReview'}
            <li class="tl-event">
              <span class="dot {it.__typename === 'MergedEvent' ? 'merged' : ''}"></span>
              <b>{it.actor?.login ?? 'someone'}</b> {words[it.__typename]}
              <span class="muted">· {relTime(it.createdAt)}</span>
            </li>
          {/if}
          {/if}
        {/each}
      </ol>
      <div class="card reply-box">
        <textarea
          aria-label="Comment on the pull request"
          placeholder={toReview ? 'Comment on the pull request… (held as a draft for your review)' : 'Add to the conversation… (posts immediately)'}
          bind:value={comment}
          oninput={() => saveDraft(draftKey, comment)}
          {@attach commentKeys((c) => (c === 'submit' ? void submitComment() : false))}
        ></textarea>
        <div class="sheet-actions">
          <span class="muted small"
            >{toReview
              ? 'Markdown. Held with your drafts and sent in your review’s body; Post now sends it to the conversation at once.'
              : 'Markdown supported. Inline comments go in your pending review from the Files view.'}</span
          >
          {#each askable as a (a.id)}
            <button class="btn" onclick={() => askAgent(a.id)} disabled={!comment.trim() || asking} title="Ask {a.name} about the pull request: only you see the answer, here">Ask {a.name}</button>
          {/each}
          {#if toReview}
            <button class="btn" onclick={postComment} disabled={!comment.trim() || pr.readOnly} title="Post it to the conversation now, outside your review">Post now</button>
            <button class="btn primary" onclick={addToReview} disabled={!comment.trim()} title="Hold it for your review (Ctrl/⌘+Enter)">Add to review</button>
          {:else}
            <button class="btn primary" onclick={postComment} disabled={!comment.trim() || pr.readOnly} title={readOnlyTitle}>Comment</button>
          {/if}
        </div>
        {#if askError}<p class="error small" role="alert">{askError}</p>{/if}
        {#if agentOpen}<RelayThread bind:this={agentPanel} {pr} about={prAbout} />{/if}
      </div>
    </section>
  </div>

  {#if wide}
    <aside class="ov-left ov-rail" id="ov-links" aria-label="Links">
      {@render linksCard()}
    </aside>
  {/if}

  <!-- After the Links and before the cards in the tab order: the handles between them. -->
  {#if wide}
    {@render resizer('left', entries.length ? 'Resize the outline and Links' : 'Resize the Links')}
    {@render resizer('right', 'Resize the merge and review cards')}
  {:else}
    {@render resizer('side', 'Resize the merge and review cards')}
  {/if}
  <aside class="ov-side ov-rail" id="ov-cards" aria-label="Merge and review">
    {#if data.state === 'OPEN'}
      <section
        id="card-merge"
        class="side-card merge"
        class:not-ready={!ready.ready}
        class:flash={flashed === 'merge'}
        class:is-pending={!!merging}
        aria-busy={merging ? 'true' : undefined}
        aria-labelledby="card-merge-h"
      >
        <h3 id="card-merge-h" tabindex="-1">{ready.ready ? 'Ready to merge' : mergeAllowed ? 'Merge' : 'Not mergeable yet'}</h3>
        {#if mergeAllowed}
          {#if missing.length}
            <p class="merge-note">GitHub would merge this, but it is not ready: {missing.join('; ')}.</p>
          {/if}
          {#if methods.length > 1}
            <select aria-label="Merge method" bind:value={method} disabled={!!merging}>
              {#each methods as m}<option value={m![0]}>{m![1]}</option>{/each}
            </select>
          {/if}
          {#if merging}
            <PendingTag label={merging.label} />
          {:else}
            <button class="btn {ready.ready ? 'go' : ''}" disabled={!!pr.busy || pr.readOnly} title={readOnlyTitle} onclick={mergeNow}>
              {methods.find((m) => m![0] === method)?.[1] ?? 'Merge'}
            </button>
          {/if}
        {:else}
          <ul class="merge-why">
            {#each whyNot as reason}<li>{reason}</li>{/each}
          </ul>
        {/if}
      </section>
    {/if}

    <section id="card-attention" class="side-card" class:flash={flashed === 'attention'} aria-labelledby="card-attention-h">
      <h3 id="card-attention-h" tabindex="-1">Attention</h3>
      <p class="muted small">Whose turn it is. Several people can be waiting at once.</p>
      {#if pr.partial}<p class="counting-note small">Worked out once the whole timeline is in.</p>{/if}
      <AttentionControls {pr} layout="list" />
      {#if canClearOverrides}
        <button class="linkish clear-overrides" onclick={() => pr.changeAttention(pr.overrides.map((o) => ({ login: o.login, state: null })))}
          >Clear manual changes</button
        >
      {/if}
    </section>

    <section id="card-reviewers" class="side-card" class:flash={flashed === 'reviewers'} aria-labelledby="card-reviewers-h">
      <h3 id="card-reviewers-h" tabindex="-1">Reviewers</h3>
      <ul class="people">
        {#each data.latestReviews.nodes as r (r.id)}
          <li>
            <b>{r.author?.login ?? 'ghost'}</b>
            <span class="pill {r.state === 'APPROVED' ? '' : r.state === 'CHANGES_REQUESTED' ? 'bad' : 'neutral'}">{reviewWord[r.state] ?? r.state}</span>
          </li>
        {/each}
        {#each requested as login}
          <li><b>{login}</b><span class="pill pending">requested</span></li>
        {/each}
        {#if pr.writes.on(subject.reviewRequest)}<li><PendingTag label={pr.writes.on(subject.reviewRequest)!.label} /></li>{/if}
        {#if !data.latestReviews.nodes.length && !requested.length}<li class="muted">No reviewers yet</li>{/if}
      </ul>
      {#if data.state === 'OPEN'}
        <form class="inline-form" onsubmit={(e) => { e.preventDefault(); if (newReviewer.trim()) { void pr.requestReviewer(newReviewer.trim().replace(/^@/, '')); newReviewer = ''; } }}>
          <input type="text" aria-label="Request a review from" placeholder="Request review: login" bind:value={newReviewer} disabled={pr.readOnly} title={readOnlyTitle} />
          <button class="btn small" disabled={!newReviewer.trim() || pr.readOnly} title={readOnlyTitle}>Request</button>
        </form>
      {/if}
    </section>

    <!-- Read-only: labels are put on and taken off on GitHub. -->
    <section id="card-labels" class="side-card" aria-labelledby="card-labels-h">
      <h3 id="card-labels-h">Labels</h3>
      {#if data.labels.nodes.length}
        <p class="labels-all"><LabelList labels={data.labels} /></p>
      {:else}
        <p class="muted small">None</p>
      {/if}
      <p class="muted small"><a href={data.url} target="_blank" rel="noreferrer">Change labels on GitHub ↗</a></p>
    </section>

    <section id="card-checks" class="side-card" class:flash={flashed === 'checks'} aria-labelledby="card-checks-h" aria-busy={pr.partial}>
      <h3 id="card-checks-h" tabindex="-1">Checks <span class="pill {checksTone(pr.checksState) === 'good' ? '' : checksTone(pr.checksState)}">{pr.checksState?.toLowerCase() ?? 'none'}</span></h3>
      <ul class="checks-list">
        {#each pr.checks as c, i (i)}
          {@const st = checkState(c)}
          {@const url = httpUrl(c.__typename === 'CheckRun' ? c.detailsUrl : c.targetUrl)}
          <li>
            <span class="dot {['SUCCESS'].includes(st) ? 'good' : ['FAILURE', 'ERROR', 'TIMED_OUT', 'ACTION_REQUIRED', 'CANCELLED'].includes(st) ? 'bad' : ['SKIPPED', 'NEUTRAL'].includes(st) ? '' : 'pending'}"></span>
            {#if url}<a href={url} target="_blank" rel="noreferrer">{checkName(c)}</a>{:else}<span>{checkName(c)}</span>{/if}
            <span class="muted mono">{st.toLowerCase()}</span>
          </li>
        {:else}
          <li class={pr.partial ? 'counting-note' : 'muted'}>{pr.partial ? 'Checks are still loading…' : 'No checks reported'}</li>
        {/each}
      </ul>
      {#if pr.partial && pr.checks.length && pr.checksTotal > pr.checks.length}
        <p class="counting-note small checks-more">Showing {pr.checks.length} of {pr.checksTotal} checks; the rest are still loading.</p>
      {:else if pr.checksTotal > pr.checks.length}
        <p class="muted small checks-more">
          Showing {pr.checks.length} of {pr.checksTotal} checks. <a href="{data.url}/checks" target="_blank" rel="noreferrer">All checks on GitHub ↗</a>
        </p>
      {/if}
    </section>

    {#if session.agents}<RelayPrAgents subject={pullKey(pr.owner, pr.repo, pr.number)} />{/if}

    {#if !wide}{@render linksCard()}{/if}
  </aside>
</div>
<RefLinks root={overviewEl} ctx={linkCtx} />
<DescNotes
  bind:this={descNotes}
  {pr}
  root={descText}
  onjump={(id) => void jump({ kind: 'comment', id })}
  agents={askable}
  onask={(to, text, quote) => askAgent(to, { text, quote })}
/>

<style>
  /* Outline of the description */
  .outline ol {
    list-style: none;
    margin: 8px -8px 0;
    padding: 0;
    display: grid;
    gap: 1px;
  }
  .outline button {
    display: block;
    width: 100%;
    text-align: left;
    border: 0;
    border-left: 2px solid transparent;
    border-radius: 0 6px 6px 0;
    background: none;
    padding: 4px 8px 4px calc(8px + var(--depth) * 12px);
    color: var(--muted);
    font-size: 13px;
    line-height: 1.35;
    cursor: pointer;
  }
  .outline li.outermost button {
    color: var(--fg);
  }
  .outline button:hover {
    background: var(--soft);
    color: var(--fg);
  }
  .outline button.current {
    border-left-color: var(--accent);
    background: var(--soft);
    color: var(--accent);
  }
  .description :global(:is(h1, h2, h3, h4, h5, h6)) {
    scroll-margin-top: calc(var(--topbar-h) + 16px);
  }
  .description :global(:is(h1, h2, h3, h4, h5, h6):focus:not(:focus-visible)) {
    outline: none;
  }
  .description :global(:is(h1, h2, h3, h4, h5, h6):focus-visible) {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
    border-radius: 3px;
  }
  /* An outline entry led here: a brief accent wash. */
  .description :global(.heading-flash) {
    animation: heading-flash 1.8s ease-out;
    border-radius: 4px;
  }
  @keyframes heading-flash {
    0%,
    35% {
      background: var(--soft);
      box-shadow: 0 0 0 6px var(--soft);
    }
    100% {
      background: transparent;
      box-shadow: 0 0 0 6px transparent;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .description :global(.heading-flash) {
      animation: none;
      background: var(--soft);
    }
  }

  /* Commits in the conversation */
  .tl-sha {
    text-decoration: none;
  }
  .tl-sha code {
    color: var(--accent);
  }
  .tl-sha:hover code {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .tl-github {
    color: var(--muted);
    font-size: 11px;
    text-decoration: none;
    padding: 0 2px;
  }
  .tl-github:hover {
    color: var(--accent);
  }
  .run-toggle {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    border: 0;
    background: none;
    padding: 2px 0;
    color: var(--fg);
    font: inherit;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }
  .run-toggle::before {
    content: '▸';
    display: inline-block;
    color: var(--muted);
    font-size: 13px;
    line-height: 1;
    transition: transform 0.15s;
  }
  .tl-run.open .run-toggle::before {
    transform: rotate(90deg);
  }
  .run-toggle:hover {
    color: var(--accent);
  }
  .run-diff {
    font-size: 12px;
  }
  .run-commits {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }
  @media (prefers-reduced-motion: reduce) {
    .run-toggle::before {
      transition: none;
    }
  }

  .side-card h3:focus {
    outline: none;
  }
  .side-card h3:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
    border-radius: 3px;
  }
  /* A header item led here: a brief accent ring. */
  .side-card.flash {
    animation: card-flash 1.8s ease-out;
  }
  @keyframes card-flash {
    0%,
    35% {
      box-shadow: 0 0 0 3px var(--accent);
      border-color: var(--accent);
    }
    100% {
      box-shadow: 0 0 0 3px transparent;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .side-card.flash {
      animation: none;
      box-shadow: 0 0 0 3px var(--accent);
    }
  }
  .merge-why {
    margin: 8px 0 0;
    padding-left: 18px;
    font-size: 13px;
    display: grid;
    gap: 4px;
  }
  .clear-overrides {
    margin-top: 10px;
  }

  /* Links card */
  .links-count {
    font-weight: 400;
    margin-right: auto;
  }
  .link-groups,
  .link-entries,
  .link-mentions {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .link-groups {
    display: grid;
    gap: 8px;
    margin-top: 8px;
  }
  .link-group {
    margin: 0 0 3px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--muted);
    overflow-wrap: anywhere;
  }
  .link-entries {
    display: grid;
    gap: 4px;
  }
  .link-entry {
    display: grid;
    gap: 4px;
    min-width: 0;
    font-size: 13px;
  }
  .link-head {
    /* Holds a preview's .sr-only note. */
    position: relative;
    display: flex;
    gap: 6px;
    align-items: baseline;
    min-width: 0;
  }
  .link-target {
    /* Holds its .sr-only text, which would otherwise widen the page. */
    position: relative;
    /* Gives way (ellipsis) only once the title has, so a state chip and the mention count stay in the card. */
    flex: 0 1 auto;
    min-width: 3em;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .link-target .ext {
    margin-left: 2px;
    font-size: 11px;
  }
  .ref-state {
    flex: none;
    font-size: 11px;
    line-height: 1.6;
    padding: 0 7px;
    border-radius: 30px;
    white-space: nowrap;
    background: var(--goodbg);
    color: var(--good);
  }
  .ref-state.merged {
    background: var(--mergedbg);
    color: var(--merged);
  }
  .ref-state.closed {
    background: var(--badbg);
    color: var(--bad);
  }
  .ref-state.pv-stale,
  .ref-state.pv-unverified,
  .ref-state.pv-inactive {
    background: var(--code);
    color: var(--muted);
  }
  .ref-state.pv-pending {
    background: var(--warnbg);
    color: var(--warn);
  }
  .ref-state.pv-failed {
    background: var(--badbg);
    color: var(--bad);
  }
  .ref-state.draft {
    background: var(--code);
    color: var(--muted);
  }
  .link-title {
    flex: 0 1000 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--muted);
  }
  .link-mentions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .mention {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    font-size: 11px;
    line-height: 1.6;
    border: 1px solid var(--line);
    border-radius: 30px;
    padding: 0 8px;
    background: none;
    color: var(--muted);
    text-decoration: none;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
  }
  .mention:hover,
  .mention:focus-visible {
    color: var(--accent);
    border-color: var(--accent);
  }
  /* An entry's mention count, which lists its mentions. */
  .mentions-toggle {
    flex: none;
    margin-left: auto;
    min-width: 24px;
    min-height: 24px;
    border: 1px solid var(--line);
    border-radius: 30px;
    padding: 0 6px;
    background: none;
    color: var(--muted);
    font-size: 11px;
    line-height: 1.6;
    cursor: pointer;
  }
  .mentions-toggle::after {
    content: ' ▾';
  }
  .mentions-toggle.open::after {
    content: ' ▴';
  }
  .mentions-toggle:hover,
  .mentions-toggle:focus-visible,
  .mentions-toggle.open {
    color: var(--accent);
    border-color: var(--accent);
  }
  .links-more {
    margin-top: 8px;
  }

  /* A mention jumped to from the Links card: a brief accent ring. */
  .description:focus,
  .tl-card:focus {
    outline: none;
  }
  .description:focus-visible,
  .tl-card:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  .jump-flash {
    animation: card-flash 1.8s ease-out;
  }
  @media (prefers-reduced-motion: reduce) {
    .jump-flash {
      animation: none;
      box-shadow: 0 0 0 3px var(--accent);
    }
  }
</style>
